import { Injectable } from '@angular/core';
import { Environment } from '../environment';
import {
  getDomain,
  getEnvironment,
  getPort,
  getUrlWithProtocolTenantAndDomain,
  getUrlWithTenantAndDomain
} from '../helpers/hostname-utils';

// eslint-disable-next-line @typescript-eslint/naming-convention, no-shadow
export enum ENV_MOCK_KEYS {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MOCK_REST = 'MOCK_REST'
}

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  constructor() {}

  /**
   * Provides a full URL for itonics.io domain
   * with the given path, the same environment as the
   * code executed or a specified one and the protocol by default or the specified one.
   *
   * @param path
   * @returns full rest url
   */
  private getAPIUrl(path: string = '', protocol: 'http' | 'https' | 'ws' | 'wss' = 'https') {
    const endpoint: string = path ? (path.startsWith('/') ? path : `/${path}`) : '';

    const scheme: string = `${protocol}://`;

    const domain: string = getPort() ? getDomain() : getUrlWithTenantAndDomain();

    return [scheme, domain, '/rest/internal', endpoint].join('');
  }

  /**
   * Provides a full REST Api URL for the itonics.io domain
   *
   * @param restPath
   * @returns
   */
  public getRestUrl(restPath: string = ''): string {
    return this.getAPIUrl(restPath);
  }

  /**
   * Construct the base endpoint URL for external ODATA API calls
   */
  public getODATAEndpoint(path: string) {
    const scheme: string = `https://`;
    const domain: string = getPort() ? getDomain() : getUrlWithTenantAndDomain();
    const _path: string = `/rest/external/odata/v1${path ? (path.startsWith('/') ? path : `/${path}`) : ''}`;

    return [scheme, domain, _path].join('');
  }

  /**
   * Provides a full URL for the websocket connection
   */
  public getWSUrl(envUrl?: string, path?: string): string {
    if ((!envUrl && !window.location) || (!envUrl && window.location.hostname === 'localhost')) {
      throw new Error('The EnvironmentService requires an environment definition or a browser runtime (localhost is not supported)');
    }

    const scheme: string = `wss://`;
    const environment: Environment = envUrl === undefined || envUrl === null ? getEnvironment() : (envUrl as Environment);
    const domain: string = `itonics-ws${environment ? '-' + environment : ''}.com/`;
    const endpoint: string = path ? (path.startsWith('/') ? path : `/${path}`) : '';

    return [scheme, domain, endpoint].join('');
  }

  /**
   * Currently GraphQl repos are under the same domain than the rest endpoints
   *
   * @param graphQlPath
   * @returns
   */
  public getGraphQlUrl(graphQlPath: string = ''): string {
    return this.getAPIUrl(graphQlPath);
  }

  /**
   * Currently GraphQl repos are under the same domain than the rest endpoints
   *
   * @param graphQlPath
   * @returns
   */
  public getGraphQlWsUrl(graphQlPath: string = ''): string {
    return this.getAPIUrl(graphQlPath, 'wss');
  }

  /**
   * Provides a full URL to the given application and path (on the same environment as the code executed or a specified one)
   *
   * @param app The application prefix (eg. id, explorer, rest, gql)
   * @param path anything behind the first / (with or without the slash)
   */
  public getUrl(app: string, path: string = ''): string {
    const completePath: string = `/${app}${path ? (path.startsWith('/') ? path : `/${path}`) : ''}`;

    return [getUrlWithProtocolTenantAndDomain(), completePath].join('');
  }

  /**
   * Provides a full URL to the given application, path and tenantSlug (on the same environment as the code executed or a specified one)
   *
   * @param app The application prefix (eg. id, explorer, rest, gql)
   * @param path anything behind the first / (with or without the slash)
   * @param tenantSlug the tenant slug, will be added as subdomain
   * @returns
   */
  public getTenantUrl(app: string, path: string = '', tenantSlug: string): string {
    if (getUrlWithTenantAndDomain() === 'localhost') {
      throw new Error('The EnvironmentService requires an environment definition or a browser runtime (localhost is not supported)');
    }
    const completePath: string = `/${app}${path ? (path.startsWith('/') ? path : `/${path}`) : ''}`;

    return ['https://', `${tenantSlug}.${getDomain()}`, completePath].join('');
  }

  /**
   * In the new domain scheme FE and BE do not have the same domain
   * that means every time we sent a request to BE the cookie is not been attached
   * and files-v2 endpoint expects that, in order to by-pass this,
   * infrastructure team has created an endpoint with the frontend domain
   *
   * We expect to use this url only for files-v2 endpoint
   *
   * @returns a string
   */
  public getFilesUrl(path: string = ''): string {
    return this.getAPIUrl(path);
  }

  /**
   * Provides the URL to ID app without tenant slug as subdomain
   *
   * @returns URL as string
   */
  public getIdAppUrlWithoutTenantSlug(): string {
    const environment: Environment = getEnvironment();
    return `https://itonics${environment ? `-${environment}` : ''}.io/id`;
  }
}
